import { type ProductOption } from "@kanpla/api-contract";

import { type FlexMenu, type FlexProduct } from "~/api/menus/types";

import { type PreOrderLine } from "../__app.collect.orders.create/orders-create-context";
import { pricePreOrderLine } from "../__app.collect.orders.create/price-pre-order-line";

import { parseVariantFormData } from "./parse-variant-form-data";
import { type ProductFormData } from "./route";

type PickedVariantsArgs = {
  product: FlexProduct;
  variants: ProductOption[];
  discounts: FlexMenu["discounts"];
  selectedVariants: ProductFormData["variants"];
};

type BuildPreOrderCandidateArgs = PickedVariantsArgs & {
  moduleId: string;
};

function pricePicks({
  product,
  variants,
  discounts,
  selectedVariants
}: PickedVariantsArgs) {
  const choices = parseVariantFormData(selectedVariants);

  return {
    choices,
    priced: pricePreOrderLine(
      product,
      variants,
      discounts,
      Object.fromEntries(choices.map((choice) => [choice.id, choice.quantity]))
    )
  };
}

export function buildPreOrderCandidate({
  moduleId,
  ...args
}: BuildPreOrderCandidateArgs): PreOrderLine {
  const { choices, priced } = pricePicks(args);

  return {
    id: args.product.id,
    moduleId,
    unitPriceInclVat: priced.unitPriceInclVat,
    depositInclVat: priced.depositInclVat,
    choices
  };
}

/**
 * What the picked choices add to the bill: their extras, discounted like the
 * product they belong to, and their deposits. The product's own discount is
 * not theirs to show.
 */
export function previewPickedExtrasInclVat(args: PickedVariantsArgs): number {
  const { priced } = pricePicks(args);
  const bare = pricePreOrderLine(
    args.product,
    args.variants,
    args.discounts,
    {}
  );

  return (
    priced.unitPriceInclVat -
    bare.unitPriceInclVat +
    priced.variantDeposits.inclVat
  );
}
